import i18n, { type TOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import type { TranslatedString } from "@Helpers/types/i18n";

// Re-export types from helpers
export type { TranslatedString } from "@Helpers/types/i18n";
export { untranslated } from "@Helpers/types/i18n";

// Re-export useTranslation - it now returns TranslatedString due to module augmentation
export { useTranslation } from "react-i18next";

/**
 * Module augmentation to make i18next's t() return TranslatedString.
 * This allows using `useTranslation()` directly without needing a wrapper.
 */
declare module "i18next" {
  interface TFunction {
    (key: string | string[], options?: Record<string, unknown>): TranslatedString;
  }
}

declare module "react-i18next" {
  export function useTranslation(): {
    t: (key: string, options?: Record<string, unknown>) => TranslatedString;
    i18n: typeof i18n;
  };
}

// Detect whether it's running in React Native
const isReactNative = typeof navigator !== "undefined" && navigator.product === "ReactNative";

const getPreferredLanguage = () => {
  if (isReactNative) {
    return "en";
  }
  return globalThis.localStorage?.getItem("preferredLanguage") ?? "en";
};

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: getPreferredLanguage(),
    backend: {
      loadPath: "/locales/{{lng}}.json",
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: !isReactNative,
    },
    defaultNS: "common",
    ns: [],
  });

/**
 * Typed translation function that returns a TranslatedString.
 * Use this function to ensure toast notification content is properly internationalized.
 *
 * @example
 * import { t } from "@i18n";
 * dispatch(showToastNotification({ content: t("success.message") }));
 */
export function t(key: string, options?: TOptions): TranslatedString {
  return i18n.t(key, options) as TranslatedString;
}

export default i18n;
